import { isEmpty, map, max, orderBy } from 'lodash';
import { getters as storeGetters } from '@/modules/core/app/helpers/store';
import { WidgetFactory } from '@/modules/ta/widget/factories/WidgetFactory';

export const getters = {
  /**
   * @param state
   * @returns {boolean}
   */
  needsReload(state) {
    return state.needsReload;
  },
  /**
   * @param state
   * @returns {boolean}
   */
  areFiltersInitialized(state) {
    return state.filtersInitialized;
  },
  /**
   * @param state
   * @returns {Array<DashboardFilterOption>}
   */
  getFilterOptions(state) {
    return state.filterOptions;
  },
  /**
   * @param state
   * @returns {Array<DashboardFilterOption>}
   */
  getActiveDashboardFilterOptions(state) {
    return state.activeDashboardFilterOptions;
  },
  /**
   * @param state
   * @returns {DashboardFilters}
   */
  getFilters(state) {
    if (!state.filters) {
      return null;
    }

    return state.filters.getFilters();
  },
  /**
   * @param state
   * @returns {DashboardFilterItem}
   */
  getCurrentActiveFilter(state) {
    if (!state.filters) {
      return null;
    }

    return state.filters.getActiveFilter();
  },
  /**
   * @param state
   * @returns {boolean}
   */
  isClientFilter(state) {
    if (!state.filters) {
      return null;
    }
    return state.filters.isClientFilter();
  },
  /**
   * @param state
   * @returns {boolean}
   */
  isClientGroupFilter(state) {
    if (!state.filters) {
      return null;
    }
    return state.filters.isClientGroupFilter();
  },
  /**
   * @param state
   * @returns {boolean}
   */
  isClusterFilter(state) {
    if (!state.filters) {
      return null;
    }
    return state.filters.isClusterFilter();
  },
  /**
   * @param state
   * @returns {boolean}
   */
  hasFilters(state) {
    // Before any filter is set, empty filters have this structure: {data: null}
    if (!state.filters) {
      return false;
    }
    // And afterwards it can be empty this way: {data: {client: {..., values: []}}}
    return state.filters.hasFilters();
  },
  executiveSummaryIsEnabled(state) {
    return state.dashboard.executive_summary_enabled;
  },
  getDashboard(state) {
    return state.dashboard;
  },
  getLayoutsInDashboard(state) {
    if (!state.dashboard) {
      return [];
    }
    const layoutIds = Object.keys(state.dashboard.layouts);
    return storeGetters.dashboardLayout.getLayoutsFromIds(layoutIds);
  },
  getFirstLayout() {
    const ordered = storeGetters.dashboard.getOrderedLayoutsInDashboard();
    return Object.values(ordered).pop();
  },
  getOrderedLayoutsInDashboard() {
    return orderBy(storeGetters.dashboard.getLayoutsInDashboard(), 'display_order');
  },
  getOrderedLayoutsWithHydratedWidgets() {
    const layouts = storeGetters.dashboard.getOrderedLayoutsInDashboard();
    return layouts.map((layout) => ({
      ...layout,
      widgets: Object.fromEntries(
        storeGetters.dashboardLayout
          .getOrderedWidgetsInLayout(layout.id)
          .map((widget) => [
            widget.id,
            widget?.isMediaWidget() ? widget : WidgetFactory.build(widget),
          ])
      ),
    }));
  },
  getMetadata(state) {
    return state.dashboard?.metadata;
  },
  dashboardShouldAppendDatagrids(state) {
    const metadata = state.dashboard?.metadata;

    return metadata
      ? metadata.append_exported_datagrids || metadata.append_exported_datagrids_to_layout
      : false;
  },
  /**
   * @param state
   * @returns {SelectedEntity|null}
   */
  getSelectedEntity(state) {
    return state.dashboard?.metadata.selected_entity;
  },
  getCurrentLayoutId(state) {
    return state.currentLayoutId;
  },
  /**
   * Returns the next layout display order
   * @returns {number}
   */
  getNextLayoutDisplayOrder() {
    const layouts = storeGetters.dashboard.getLayoutsInDashboard();
    return !isEmpty(layouts) ? max(map(layouts, 'display_order')) + 1 : 1;
  },
  /**
   * @returns {WidgetHighlightOption[]}
   */
  getHighlightedWidgetsOptions(state) {
    return state.highlightedWidgetsOptions;
  },
  getExportLayoutOptions(state) {
    return state.exportLayoutOptions;
  },
  isLayoutHydrated(state, layoutId) {
    return state.hydratedLayouts.includes(layoutId);
  },
  hasNonChatGPTWidgets(state) {
    return state.dashboard?.has_non_chatgpt_widgets;
  },
};
